import { toastr } from 'react-redux-toastr';
import { format, formatDuration, intervalToDuration, isAfter, isBefore, isValid, set } from 'date-fns';
import i18next from 'i18next';

import { errorConfig } from '@/config/i18n';
import { Vehicle } from '@/config/types/Vehicles';
import { localeMap } from '../../index';

export const timeToDate = (time: string): Date => {
  if (!time) {
    return null;
  }
  const [hours, minutes] = time.split(':');

  const check: Date = set(new Date(), {
    hours: Number(hours),
    minutes: Number(minutes),
  });
  if (check instanceof Date && isValid(check)) {
    const y = check.getFullYear();
    const mo = check.getMonth()+1; //getMonth gives months from 0 to 11
    const d = check.getDate();
    const h = check.getHours();
    const m = check.getMinutes();
    const s = check.getSeconds();
    const ss = check.getMilliseconds();
    // add Z to a Date to be considered an ISO string, then new Date() converts it to local time
    return new Date(`${y}-${mo<10 ? `0${mo}`: mo}-${d<10 ? `0${d}`: d}T${(h>=0 && h<10) ? `0${h}`: h}:${(m>=0 && m<10) ?
       `0${m}`: m}:${(s>=0 && s<10)? `0${s}`: s}.${(ss>=0 && ss< 10) ? `0${ss}`: ss}Z`);
  }
  const date = localDateYearShort(new Date());
  return new Date(`${date}'T'${time}`);
};

export const dateToTime = (date: Date): string | null => {
  return isValid(date) 
    ? localTime(date) 
    : null;
};

export const validTimeChange = (vehicle: Vehicle, name: string, date: Date): boolean => {
  const { departure_time_from, departure_time_to } = vehicle;
  if (name === 'return_time_from' && departure_time_to) {
    const valid = isBefore(date, timeToDate(departure_time_to));
    if (!valid) toastr.error(i18next.t('common:vehicleEditor.edit'), i18next.t('err:time.depatureFirst'), errorConfig);
    return valid;
  }

  if (name === 'departure_time_to' && departure_time_from) {
    const valid = isAfter(date, (timeToDate(departure_time_from)));
    if (!valid) toastr.error(i18next.t('common:vehicleEditor.edit'), i18next.t('err:time.depatureFirst'), errorConfig);
    return valid;
  }
  return true;
};

/**
 *
 * @param {Date} date
 * @param {Date} oldDate
 * @returns {Date}
 */
export const newDateSameTime = (date: Date, oldDate: Date): Date => {
  const newDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    oldDate.getHours(),
    oldDate.getMinutes(),
    oldDate.getSeconds()
  );
  return newDate;
};

/**
 *
 * @param {Date} date
 * @param {Date} oldDate
 * @returns {Date}
 */
export const newTimeSameDate = (date: Date, oldDate: Date): Date => new Date(
  oldDate.getFullYear(),
  oldDate.getMonth(),
  oldDate.getDate(),
  date.getHours(),
  date.getMinutes(),
);

/**
 *
 * @param {Number} seconds
 * @returns {String} a readable duration without seconds
 */
export const readableSeconds = (seconds: number): string => {
  if(seconds === 0) {
    return `0 ${localeMap[i18next.language] === 'en' ? 'Minutes' : 'Minuten'}`;
  }
  if(seconds<60){
    seconds=60;
  }
  return formatDuration(intervalToDuration({
    start: 0,
    end: seconds * 1000,
  }), {
    locale: localeMap[i18next.language],
    format: ['days', 'hours', 'minutes']
  });
};

/**
 *
 * @param {Date} date
 * @returns {string} 12:00 AM
 */
export const localTime = (date: Date): string => {
  if(!isValid(date)) return '';
  return format(date, 'p', {locale: localeMap[i18next.language]});
};
/**
 *
 * @param {Date} date
 * @returns {string} 04/29/1453, 12:00 AM
 */
export const localDateTime = (date: Date): string => {
  if(!isValid(date)) return '';
  return format(date, 'Pp', {locale: localeMap[i18next.language]});
};
/**
 *
 * @param {Date} date
 * @returns {string} Apr 29, 1453
 */
export const localDateYear = (date: Date): string => {
  if(!isValid(date)) return '';
  return format(date, 'PP', {locale: localeMap[i18next.language]});
};
/**
 *
 * @param {Date} date
 * @returns {string} 04/29/1453
 */
export const localDateYearShort = (date: Date): string => {
  if(!isValid(date)) return '';
  return format(date, 'P', {locale: localeMap[i18next.language]});
};
export const updated_timeToDate = (isoString: string): Date => {
  if (!isoString) {
    return null;
  }
  // Parse the ISO string to get the time (HH:mm)
  const [date, timeWithOffset] = isoString.split('T');
  const [time, offset] = timeWithOffset.split('+');
  const [hours, minutes] = time.split(':'); // Split into hours and minutes
  // Log for debugging
  const currentDate = new Date();
  const check: Date = set(new Date(currentDate), {
    hours: Number(hours),
    minutes: Number(minutes),
    seconds: 0,
    milliseconds: 0
  });

  if (check instanceof Date && isValid(check)) {
    const y = check.getFullYear();
    const mo = check.getMonth() + 1;
    const d = check.getDate();
    const h = check.getHours();
    const m = check.getMinutes();
    const s = check.getSeconds();
    const ss = check.getMilliseconds();
    return new Date(`${y}-${mo < 10 ? `0${mo}` : mo}-${d < 10 ? `0${d}` : d}T${(h >= 0 && h < 10) ? `0${h}` : h}:${(m >= 0 && m < 10) ? `0${m}` : m}:${(s >= 0 && s < 10) ? `0${s}` : s}.${(ss >= 0 && ss < 10) ? `0${ss}` : ss}Z`);
  }
  // If invalid, return the local date with the extracted time
  const dateOnly = new Date();
  return new Date(`${dateOnly.toISOString().split('T')[0]}T${time}`);
};
export const readableDuration = (start_time: string, end_time: string | null): string => {
  if (!start_time || !end_time) {
    return '--';
  }
  const startDate = new Date(start_time);
  const endDate = new Date(end_time);
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return '--';
  }
  const diffInSeconds = (endDate.getTime() - startDate.getTime()) / 1000;
  if (diffInSeconds <= 0) {
    return `--`;
  }
  if (diffInSeconds < 60) {
    return `1 ${localeMap[i18next.language] === 'en' ? 'Minute' : 'Minute'}`;
  }
  return formatDuration(
    intervalToDuration({
      start: 0,
      end: diffInSeconds * 1000,
    }),
    {
      locale: localeMap[i18next.language],
      format: ['days', 'hours', 'minutes'],
    }
  );
};
/**
 * https://date-fns.org/v2.27.0/docs/format
*/
import { parse } from 'date-fns';

export const parseTimeStringToDate = (timeStr: string): Date => {
  return new Date(`1970-01-01T${timeStr}Z`);
};